import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import App from './App';
import { connect, Provider } from 'react-redux';
import { history, persistor, store } from 'utils/configureStore';
import { ApolloProvider } from 'react-apollo';
import { generateApolloClient } from 'utils/configureApollo';
import { PersistGate } from 'redux-persist/integration/react';
import { pathOr } from 'ramda';
import { ThemeProvider } from 'styled-components';
import { theme } from 'constants/styled';
import 'rc-slider/assets/index.css';
import 'normalize.css';
import './index.css';
import { ConnectedRouter as Router } from 'connected-react-router';
import { useLocation, useHistory } from 'react-router-dom';
import GDPRModal from 'components/GDPRModal';



async function getCountryCodeFromIP(): Promise<string | null> {
  const services = [
    async () => { 
      const r = await fetch('https://ipapi.co/json/');    
      return (await r.json()).country_code; 
    },

    async () => { 
      const r = await fetch('https://ipwho.is/');         
      const d = await r.json(); 
      if (!d.success) {
        throw new Error();
      } 
      return d.country_code; 
    },

    async () => { 
      const r = await fetch('https://api.db-ip.com/v2/free/self'); 
      return (await r.json()).countryCode; 
    },

    async () => { 
      const r = await fetch('https://geolocation-db.com/json/'); 
      return (await r.json()).country_code; 
    },
  ];

  for (const fn of services) {
    try {
      const code = await fn();
      if (typeof code === 'string') return code.toUpperCase();
    } catch {}
  }
  return null;
}

function getRegionFromCountryCode(code: string): 'EU' | 'US' | 'Other' {
  const EU = new Set([
    'AT','BE','BG','HR','CY','CZ','DK','EE','FI','FR','DE','GR','HU',
    'IE','IT','LV','LT','LU','MT','NL','PL','PT','RO','SK','SI','ES','SE',
  ]);
  if (code === 'US') return 'US';
  if (EU.has(code))  return 'EU';
  return 'Other';
}

function AppWithGDPRWrapper(props: any) {
  const [showGDPR, setShowGDPR] = useState(false);
  const location = useLocation();
  const historyHook = useHistory();

  useEffect(() => {
    if (localStorage.getItem('gdpr_consent')) return;

    (async () => {
      let code = localStorage.getItem('user_country_code');
      if (!code) {
        code = await getCountryCodeFromIP();
        if (code) localStorage.setItem('user_country_code', code);
        else return;
      }
      if (getRegionFromCountryCode(code) === 'EU') {
        setShowGDPR(true);
      }
    })();
  }, [location.pathname, historyHook]);

  const accept = () => {
    localStorage.setItem('gdpr_consent', 'accepted');
    localStorage.setItem('gdpr_consent_at', new Date().toISOString());
    setShowGDPR(false);
  };

  return (
    <>
      <App {...props} />
      {showGDPR && <GDPRModal onAccept={accept} />}
    </>
  );
}

const ApolloWrapper = connect((state: any) => ({
  token:  pathOr(null, ['user','token'], state),
  role:   pathOr(null, ['user','role'],  state),
  signed: pathOr(null, ['user','signed'],state),
  email:  pathOr(null, ['user','email'], state),
}))(({ token, role, signed, email }) => (
  <ApolloProvider client={generateApolloClient({ token })}>
    <AppWithGDPRWrapper role={role} token={token} signed={signed} email={email} />
  </ApolloProvider>
));

ReactDOM.render(
  <ThemeProvider theme={theme}>
    <Provider store={store as any}>
      <PersistGate loading={null} persistor={persistor}>
        <Router history={history}>
          <ApolloWrapper />
        </Router>
      </PersistGate>
    </Provider>
  </ThemeProvider>,
  document.getElementById('root')
);
